import { createSlice } from '@reduxjs/toolkit';
import { MOCK_PROMPT_TEMPLATES, PROMPT_TYPES, MOCK_PROMPT_TYPES } from '../shared/mockData';

const initialState = {
  templates: MOCK_PROMPT_TEMPLATES,
  promptTypes: PROMPT_TYPES,
  types: MOCK_PROMPT_TYPES,
  loading: false,
  error: null
};

const promptTemplateSlice = createSlice({
  name: 'promptTemplates',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    addTemplate: (state, action) => {
      const newTemplate = {
        ...action.payload,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      
      // If new template is set as active, deactivate all others of same type
      if (newTemplate.isActive) {
        state.templates.forEach(template => {
          if (template.type === newTemplate.type) {
            template.isActive = false;
          }
        });
      } else {
        // If this is the first template of its type, make it active
        const existingTemplatesOfType = state.templates.filter(t => t.type === newTemplate.type);
        if (existingTemplatesOfType.length === 0) {
          newTemplate.isActive = true;
        }
      }
      
      state.templates.push(newTemplate);
    },
    updateTemplate: (state, action) => {
      const index = state.templates.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.templates[index] = { ...state.templates[index], ...action.payload };
      }
    },
    deleteTemplate: (state, action) => {
      const templateToDelete = state.templates.find(t => t.id === action.payload);
      if (templateToDelete) {
        state.templates = state.templates.filter(t => t.id !== action.payload);
        
        // If deleted template was active, make another template of same type active
        if (templateToDelete.isActive) {
          const remainingTemplatesOfType = state.templates.filter(t => t.type === templateToDelete.type);
          if (remainingTemplatesOfType.length > 0) {
            remainingTemplatesOfType[0].isActive = true;
          }
        }
      }
    },
    setActiveTemplate: (state, action) => {
      const { templateId, type, isActive } = action.payload;
      
      if (isActive) {
        // Deactivate all templates of this type
        state.templates.forEach(template => {
          if (template.type === type) {
            template.isActive = false;
          }
        });
        
        // Activate the selected template
        const template = state.templates.find(t => t.id === templateId);
        if (template) {
          template.isActive = true;
        }
      } else {
        // Deactivate the selected template
        const template = state.templates.find(t => t.id === templateId);
        if (template) {
          template.isActive = false;
        }
      }
    },
    addPromptType: (state, action) => {
      const newType = {
        ...action.payload,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      state.types.push(newType);
      state.promptTypes.push(newType.name);
    },
    updatePromptType: (state, action) => {
      const index = state.types.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        const oldName = state.types[index].name;
        state.types[index] = { ...state.types[index], ...action.payload };
        
        // Update promptTypes array
        const typeIndex = state.promptTypes.indexOf(oldName);
        if (typeIndex !== -1) {
          state.promptTypes[typeIndex] = action.payload.name;
        }

        // Keep templates grouped consistently after a type rename.
        state.templates = state.templates.map((template) =>
          template.type === oldName
            ? { ...template, type: action.payload.name }
            : template
        );
      }
    },
    deletePromptType: (state, action) => {
      const typeToDelete = state.types.find(t => t.id === action.payload);
      if (typeToDelete) {
        state.types = state.types.filter(t => t.id !== action.payload);
        state.promptTypes = state.promptTypes.filter(name => name !== typeToDelete.name);
      }
    }
  }
});

export const {
  setLoading,
  setError,
  addTemplate,
  updateTemplate,
  deleteTemplate,
  setActiveTemplate,
  addPromptType,
  updatePromptType,
  deletePromptType
} = promptTemplateSlice.actions;

export default promptTemplateSlice.reducer;